// A deliberately small markdown parser for BI dashboard markdown TILES, pure and
// tested. Produces data (blocks + inline pieces); `components/markdown.tsx` turns
// that into React nodes. Nothing here ever produces HTML, so there is no
// `dangerouslySetInnerHTML` anywhere in this path.
//
// Why not a markdown library: tile content is ingested at RUNTIME (it changes
// with no deploy of this app), so the renderer must be safe against whatever an
// author writes. Emitting React nodes for a known subset and leaving the rest as
// literal text is a stricter guarantee than sanitizing an HTML pipeline, and
// costs no dependency.
//
// Why not `@bi/shared`'s `parseInlineBold`: it is bold-only on purpose because
// MIRA and STERLING are prompted to emit only bold. Widening it would change how
// agent prose renders to buyers and talent.
//
// Scope: headings, paragraphs, inline bold/italic/code, lists, links, rules.
// Anything else (tables, blockquotes, fenced code, images) stays LITERAL TEXT.

export type MarkdownBlock =
  | { kind: "heading"; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "rule" };

/** One run of inline content. `code` and `href` are mutually exclusive in
 *  practice; `bold`/`italic` may combine with neither, either, or a link. */
export interface InlinePiece {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  href?: string;
}

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const BULLET_RE = /^\s*[-*+]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/;
// Three or more of the same rule character, alone on a line.
const RULE_RE = /^\s*([-*_])\1{2,}\s*$/;

/**
 * Split a markdown tile's source into block-level nodes.
 *
 * Consecutive non-blank paragraph lines join into ONE paragraph separated by a
 * space, which is ordinary markdown behavior and matters here: Superset tiles are
 * authored with hard-wrapped prose, so treating each line as its own paragraph
 * would double every gap.
 */
export function parseMarkdownBlocks(source: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list !== null) {
      blocks.push({ kind: "list", ordered: list.ordered, items: list.items });
      list = null;
    }
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  for (const raw of source.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (line.trim() === "") {
      flushAll();
      continue;
    }

    const rule = RULE_RE.exec(line);
    if (rule) {
      flushAll();
      blocks.push({ kind: "rule" });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      flushAll();
      const level = heading[1].length as 1 | 2 | 3 | 4 | 5 | 6;
      blocks.push({ kind: "heading", level, text: heading[2].trim() });
      continue;
    }

    // A bullet check must run before the paragraph fallback, but AFTER the rule
    // check: `***` matches the bullet pattern too, and a horizontal rule read as
    // a one-item list would render a stray bullet.
    const bullet = BULLET_RE.exec(line);
    const ordered = ORDERED_RE.exec(line);
    if (bullet || ordered) {
      flushParagraph();
      const isOrdered = ordered !== null;
      const item = (isOrdered ? ordered![1] : bullet![1]).trim();
      if (list === null || list.ordered !== isOrdered) {
        flushList();
        list = { ordered: isOrdered, items: [] };
      }
      list.items.push(item);
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushAll();
  return blocks;
}

// Ordered by precedence. Code first so its contents stay literal; `**` before `*`
// so bold is never read as two italics.
//
// Italic is `*only*`, never `_underscore_`. This is a BI tool: the prose is full
// of snake_case identifiers (`first_input_chars_bucket`, `recruiter.inbox.ingests`),
// and underscore-italics would render half of one as emphasis and eat the
// underscores — silently corrupting the exact strings a reader needs to copy.
const INLINE_RE = new RegExp(
  [
    "(`[^`\\n]+`)", // 1: inline code
    "(\\*\\*[^\\n]+?\\*\\*)", // 2: bold
    "(\\*[^*\\n]+?\\*)", // 3: italic
    "(\\[[^\\]\\n]+\\]\\([^)\\s]+\\))", // 4: [text](href)
    "(https?://[^\\s)]+)", // 5: bare absolute url
  ].join("|"),
  "g",
);

const LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;
// Trailing sentence punctuation belongs to the sentence, not the href.
const TRAILING_PUNCT = /[.,;:!?)\]}'"]+$/;

/**
 * Whether an authored href may become a real `<a href>`.
 *
 * An ALLOWLIST, never a `javascript:` blocklist: blocklists lose to casing,
 * embedded control characters, and entity tricks, while an allowlist fails
 * closed on every form it has not been taught.
 *
 * The block and inline grammar only produce text nodes. An href is the one
 * place an author's string reaches the browser AS a URL, so
 * `[click](javascript:...)` would be the single hole. Content is ingested at
 * runtime with no deploy of this app, so "we wrote it" is no guarantee.
 *
 * `//evil.example` is rejected on purpose: it looks root-relative but is
 * protocol-relative, and would silently leave the app.
 */
export function isSafeHref(href: string): boolean {
  const url = href.trim();
  if (/^https?:\/\//i.test(url)) return true;
  if (/^mailto:/i.test(url)) return true;
  if (url.startsWith("#")) return true;
  return url.startsWith("/") && !url.startsWith("//");
}

/** Split one block's text into styled runs, in order. Unmatched markers stay
 *  literal — a lone `*` is a lone `*`, not the start of a broken tag. */
export function parseInline(text: string): InlinePiece[] {
  const pieces: InlinePiece[] = [];
  let last = 0;

  for (const match of text.matchAll(INLINE_RE)) {
    const start = match.index ?? 0;
    if (start > last) pieces.push({ text: text.slice(last, start) });
    const [whole, code, bold, italic, link, url] = match;

    if (code !== undefined) {
      pieces.push({ text: code.slice(1, -1), code: true });
    } else if (bold !== undefined) {
      pieces.push({ text: bold.slice(2, -2), bold: true });
    } else if (italic !== undefined) {
      pieces.push({ text: italic.slice(1, -1), italic: true });
    } else if (link !== undefined) {
      const parsed = LINK_RE.exec(link);
      // An unsafe scheme degrades to the LITERAL source, markers and all, rather
      // than to a bare label: a reader seeing `[click here](javascript:...)` can
      // tell something was rejected, where silently dropping the target would
      // leave text that reads like a link the author expected to work.
      if (parsed && isSafeHref(parsed[2])) pieces.push({ text: parsed[1], href: parsed[2] });
      else pieces.push({ text: link });
    } else if (url !== undefined) {
      const href = url.replace(TRAILING_PUNCT, "");
      pieces.push({ text: href, href });
      const dropped = url.slice(href.length);
      if (dropped) pieces.push({ text: dropped });
    }

    last = start + whole.length;
  }

  if (last < text.length) pieces.push({ text: text.slice(last) });
  return pieces;
}
